import React, {useState, useEffect} from "react";
import {
    Box,
    Text,
    Textarea,
    Button,
    useColorModeValue,
    useToast,
    Flex,
    Spinner,
    Stack
} from "@chakra-ui/react";
import {cqAPI} from "../../lib/Auth";

const POPrompts = () => {
    const bgColor = useColorModeValue("gray.100", "gray.700");
    const cardBg = useColorModeValue("#ffffff", "#212330");
    const borderClr = useColorModeValue("gray.200", "gray.600");

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const defaultFormData = {
        prompt_suggestion_prompt: "",
        moderator_system_prompt: "",
        campaign_editor_prompt: "",
        collection_identifier_prompt: {intro: "", requirement: "", output: ""},
        explainer_prompt: {intro: "", requirement: "", definitions: "", output: ""},
        data_processor_prompt: {intro: "", guidelines: ""},
        aggregator_system_prompt: "",
        explainer_judge_guideline_prompt: "",
        query_judge_guideline_prompt: "",
        response_composer_system_prompt: "",
        html_output_prompt_section: "",
        default_failed_response_text: "",
    };


    const [formData, setFormData] = useState(defaultFormData);
    const toast = useToast();

    // ----------- LOAD PROMPTS -----------
    useEffect(() => {
        const fetchPOPrompts = async () => {
            setLoading(true);
            try {
                const response = await cqAPI.get("/prompts/po");
                if (response.type === "ok" && response.data) {
                    setFormData(response.data);
                } else {
                    console.warn("Invalid or missing response data, using defaults");
                    setFormData(defaultFormData);
                    toast({
                        title: "Warning",
                        description: "Loaded default PO Prompts because the API response was invalid.",
                        status: "warning",
                        duration: 3000,
                        isClosable: true,
                    });
                }
            } catch (error) {
                console.error("Error loading PO Prompt:", error);
                toast({
                    title: "Error",
                    description: "Failed to load PO Prompt.",
                    status: "error",
                    duration: 3000,
                    isClosable: true
                });
            } finally {
                setLoading(false);
            }
        };
        fetchPOPrompts();
    }, []);

    // ----------- SAVE PROMPTS -----------
    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await cqAPI.put("/prompts/po", formData);
            if (response.type === "ok") {
                toast({
                    title: "Saved",
                    description: "PO Prompts updated successfully.",
                    status: "success",
                    duration: 3000,
                    isClosable: true
                });
            } else {
                toast({
                    title: "Error",
                    description: response.msg || 'Failed to save!',
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error("Error saving PO Prompt:", error);
            toast({
                title: "Error",
                description: "Failed to save PO Prompt.",
                status: "error",
                duration: 3000,
                isClosable: true
            });
        } finally {
            setSaving(false);
        }
    };

    // ----------- RENDER UI -----------
    return (
        <Box bg={bgColor} minH="100dvh" w="100%">
            <Box bg={bgColor} p={6} borderRadius="md" w="100%" maxW="100%">
            <Flex justify="space-between" align="center" mb={6}>
                <Text fontSize="xl" fontWeight="bold">
                    PO Prompts
                </Text>
                <Button
                    colorScheme="blue"
                    onClick={handleSave}
                    isLoading={saving}
                    loadingText="Saving"
                >
                    Save
                </Button>
            </Flex>

            {loading ? (
                <Flex justify="center" align="center" h="200px">
                    <Spinner size="xl"/>
                </Flex>
            ) : (
                <Stack spacing={6}>
                    {/* Prompt Suggestion */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Prompt Suggestion
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.prompt_suggestion_prompt}
                            onChange={(e) =>
                                setFormData({...formData, prompt_suggestion_prompt: e.target.value})
                            }
                            placeholder="Prompt Suggestion..."
                        />
                    </Box>

                    {/* Moderator */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Moderator
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.moderator_system_prompt}
                            onChange={(e) =>
                                setFormData({...formData, moderator_system_prompt: e.target.value})
                            }
                            placeholder="Moderator System Prompt..."
                        />
                    </Box>

                    {/* Campaign Editor Prompt*/}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Campaign Editor Prompt
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.campaign_editor_prompt}
                            onChange={(e) =>
                                setFormData({...formData, campaign_editor_prompt: e.target.value})
                            }
                            placeholder="Campaign Editor Prompt..."
                        />
                    </Box>

                    {/* Collection Identifier */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Collection Identifier
                        </Text>
                        <Stack spacing={3}>
                          <Text alignSelf="start">Intro: </Text>
                          <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.collection_identifier_prompt.intro}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        collection_identifier_prompt: {
                                            ...formData.collection_identifier_prompt,
                                            intro: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Collection Identifier Intro..."
                            />
                          <Text alignSelf="start">Requirement: </Text>
                           <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.collection_identifier_prompt.requirement}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        collection_identifier_prompt: {
                                            ...formData.collection_identifier_prompt,
                                            requirement: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Collection Identifier Requirement..."
                            />
                          <Text alignSelf="start">Output: </Text>
                          <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.collection_identifier_prompt.output}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        collection_identifier_prompt: {
                                            ...formData.collection_identifier_prompt,
                                            output: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Collection Identifier Output..."
                            />
                        </Stack>
                    </Box>

                    {/* Explainer */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Explainer
                        </Text>
                        <Stack spacing={3}>
                          <Text alignSelf="start">Intro: </Text>
                          <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.explainer_prompt.intro}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        explainer_prompt: {
                                            ...formData.explainer_prompt,
                                            intro: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Explainer Intro..."
                            />
                          <Text alignSelf="start">Requirement: </Text>
                          <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.explainer_prompt.requirement}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        explainer_prompt: {
                                            ...formData.explainer_prompt,
                                            requirement: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Explainer Requirement..."
                            />
                          <Text alignSelf="start">Definitions: </Text>
                          <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.explainer_prompt.definitions}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        explainer_prompt: {
                                            ...formData.explainer_prompt,
                                            definitions: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Explainer Definitions..."
                            />
                          <Text alignSelf="start">Output: </Text>
                          <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.explainer_prompt.output}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        explainer_prompt: {
                                            ...formData.explainer_prompt,
                                            output: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Explainer Output..."
                            />
                        </Stack>
                    </Box>

                    {/* Data Processor */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Data Processor
                        </Text>
                        <Stack spacing={3}>
                          <Text alignSelf="start">Intro: </Text>
                          <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.data_processor_prompt.intro}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        data_processor_prompt: {
                                            ...formData.data_processor_prompt,
                                            intro: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Data Processor Intro..."
                            />
                          <Text alignSelf="start">Guidelines: </Text>
                          <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.data_processor_prompt.guidelines}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        data_processor_prompt: {
                                            ...formData.data_processor_prompt,
                                            guidelines: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Guidelines Requirement..."
                            />
                        </Stack>
                    </Box>

                    {/* Aggregator */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Aggregator
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.aggregator_system_prompt}
                            onChange={(e) =>
                                setFormData({...formData, aggregator_system_prompt: e.target.value})
                            }
                            placeholder="Aggregator System Prompt..."
                        />
                    </Box>

                    {/* Explainer Judge */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Explainer Judge
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.explainer_judge_guideline_prompt}
                            onChange={(e) =>
                                setFormData({...formData, explainer_judge_guideline_prompt: e.target.value})
                            }
                            placeholder="Explainer Judge Guideline Prompt..."
                        />
                    </Box>

                    {/* Query Judge */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Query Judge
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.query_judge_guideline_prompt}
                            onChange={(e) =>
                                setFormData({...formData, query_judge_guideline_prompt: e.target.value})
                            }
                            placeholder="Query Judge Guideline Prompt..."
                        />
                    </Box>

                    {/* Response Composer */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Response Composer
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.response_composer_system_prompt}
                            onChange={(e) =>
                                setFormData({...formData, response_composer_system_prompt: e.target.value})
                            }
                            placeholder="Response Composer System Prompt..."
                        />
                    </Box>

                    {/* HTML Output */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            HTML Output
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.html_output_prompt_section}
                            onChange={(e) =>
                                setFormData({...formData, html_output_prompt_section: e.target.value})
                            }
                            placeholder="HTML Output Prompt Section..."
                        />
                    </Box>

                    {/* Failed Response */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Failed Response
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.default_failed_response_text}
                            onChange={(e) =>
                                setFormData({...formData, default_failed_response_text: e.target.value})
                            }
                            placeholder="Default Failed Response Text..."
                        />
                    </Box>
                </Stack>
            )}
        </Box>
        </Box>

    );
};

export default POPrompts;